import {
  useFormContext,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import {
  FormControlLabel,
  Checkbox,
  Typography,
  FormControl,
} from "@mui/material";
import { filter } from "lodash";
import { CUSTOM_COLORS, ColorToken } from "@rmh/ui-config";
import { useTranslations } from "next-intl";
import { getValidationRules } from "./utils/getValidationRules";
import type { FormCheckboxComponents } from "./FormCheckbox";
import { CheckboxCheckedIcon, CheckboxIcon } from "./CheckboxIcon";
import { HelperMessage } from "./HelperMessage";

interface FormFormButtonGroupProps {
  className?: string;
  name: string;
  label?: string;
  required?: boolean;
  rules?: RegisterOptions;
  onChange?: (value: string) => void;
  row?: boolean;
  options: CheckboxOption[];
  layout?: "grid" | "flex";
  fontStyle?: string;
  components?: FormCheckboxComponents;
  showError?: boolean;
}

export interface CheckboxOption {
  label: string;
  value: string;
}

export const FormCheckboxGroup: React.FC<FormFormButtonGroupProps> = ({
  className,
  name,
  required = true,
  rules = {},
  options,
  layout = "flex",
  row,
  components = {
    checkIcon: <CheckboxCheckedIcon />,
    uncheckIcon: <CheckboxIcon />,
  },
  showError,
}) => {
  const t = useTranslations();
  const validationRules = getValidationRules({
    required,
    rules,
    t,
  });

  const listLayout =
    layout === "flex" ? "flex flex-wrap" : "grid grid-cols-2 gap-x-20";
  const direction = layout === "flex" && !row ? "flex-col" : "";

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const {
    field: { value: fieldValue },
  } = useController<Record<string, string[]>>({
    name,
    rules: validationRules,
    control,
  });

  return (
    <FormControl className={className ?? "flex flex-1 flex-col"}>
      <div className={`${listLayout} ${direction} ml-1 gap-2`}>
        {options.map(({ label, value }) => (
          <FormControlLabel
            className="inline-flex items-start gap-2"
            control={
              <Checkbox
                checked={fieldValue.includes(value)}
                checkedIcon={components.checkIcon}
                icon={components.uncheckIcon}
                name={name}
                onChange={() => {
                  if (fieldValue.includes(value)) {
                    setValue(
                      name,
                      filter(fieldValue, (element) => element !== value),
                      { shouldValidate: true, shouldDirty: true }
                    );
                  } else {
                    setValue(name, [...fieldValue, value], {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                size="small"
                style={{
                  color: CUSTOM_COLORS[ColorToken.DEEP_BLUE][500],
                }}
              />
            }
            key={label}
            label={
              <Typography variant="buttonM" color="text.primary">
                {label}
              </Typography>
            }
            labelPlacement="end"
            value={value}
          />
        ))}
      </div>
      <HelperMessage errors={errors} name={name} showError={showError} />
    </FormControl>
  );
};
